import { formatDate } from "date-fns";
import type {
  APIListingAgentUpdateEntry,
  APIListingApplicantFeedbackEntry,
  APIListingEventFeedbackEntry,
  EnumValue,
} from "models/types/api-listing";
import { upperFirst } from "utils/string";

export type FeedbackItem =
  | APIListingApplicantFeedbackEntry
  | APIListingAgentUpdateEntry
  | APIListingEventFeedbackEntry;

export enum FeedbackGroupByOption {
  Name = "name",
  InterestLevel = "interest-level",
  FeedbackType = "feedback-type",
  Date = "date",
}

/** Default group-by used when no explicit selection is active. */
export const DEFAULT_GROUP_BY_OPTION = FeedbackGroupByOption.Date;

type InterestLevelBucket = { id: string; label: string; value: number };

/**
 * Single source of truth for "does this listing have interest-level data worth
 * showing" — true when at least one interest-level bucket is non-zero. Shared
 * by the Overview performance card, the Feedback chart, and the Feedback
 * route-lock (locked-routes-config) so the definition can't drift. CRM-14119.
 */
export const hasNonZeroInterestLevel = (
  buckets?: InterestLevelBucket[],
): boolean => (buckets ?? []).some((bucket) => bucket.value !== 0);

/**
 * Fall back to the default (date) sort when interest-level grouping is selected
 * but interest-level data is hidden by preference. CRM-14119.
 */
export const resolveEffectiveGroupBy = (
  groupByValue: string,
  showInterestLevel: boolean,
): string =>
  !showInterestLevel && groupByValue === FeedbackGroupByOption.InterestLevel
    ? DEFAULT_GROUP_BY_OPTION
    : groupByValue;

/**
 * Ignore an interest-level "show only" filter (e.g. from a bookmarked
 * deep-link) when interest-level data is hidden by preference. CRM-14119.
 */
export const resolveEffectiveShowOnly = (
  showOnly: ShowOnlyOptions | undefined,
  showInterestLevel: boolean,
): ShowOnlyOptions | undefined =>
  !showInterestLevel && showOnly?.key === "interest_level"
    ? undefined
    : showOnly;

export type FeedbackTypeMenuOption = {
  value:
    | "enquiry"
    | "inspection"
    | "ofi"
    | "price_reduction"
    | "followup"
    | "offer";
  label: string;
};

const feedbackTypes = [
  { id: "enquiry", text: "Enquiry" },
  { id: "inspection", text: "Inspection" },
  { id: "ofi", text: "Open Home" },
  { id: "followup", text: "Follow-up" },
  { id: "agent_vendor", text: "Agent Update" },
  { id: "price_reduction", text: "Price Reduction" },
];

const feedbackTypeLabels: Record<string, string> = {
  enquiry: "Enquiry",
  inspection: "Inspection",
  ofi: "Open Home",
  followup: "Follow-up",
  agent_vendor: "Agent Update",
  price_reduction: "Price Reduction",
};

function groupByFeedbackType(feedback: FeedbackItem[]) {
  return feedbackTypes.map((feedbackType) => ({
    label: feedbackTypeLabels[feedbackType.id] || feedbackType.id,
    feedback: sortFeedbackMostRecentFirst(feedback).filter(
      (i) => i.type.id === feedbackType.id,
    ),
  }));
}

function groupByInterestLevel(feedback: FeedbackItem[]) {
  const levels = ["hot", "warm", "cold", null];

  return levels.map((interestLevel) => ({
    label: interestLevel ? upperFirst(interestLevel) : "Neutral",
    feedback: feedback.filter((i) => {
      if (!("interest_level" in i)) return interestLevel === null;

      return interestLevel
        ? i.interest_level?.id === interestLevel
        : i.interest_level === null || i.interest_level?.id === "unknown";
    }),
  }));
}

function groupByDate(feedback: FeedbackItem[]) {
  // Get all dates that have the date_of property
  const allDates = sortFeedbackMostRecentFirst(feedback)
    .filter((i) => "date_of" in i)
    .map((i) => (i as any).date_of);

  const uniqueDates = [...new Set(allDates)];

  return uniqueDates.map((date) => ({
    label: formatDate(date, "eee do MMM, yyyy"),
    feedback: feedback.filter(
      (i) => "date_of" in i && (i as any).date_of === date,
    ),
  }));
}

function getPartyFromFeedback(feedback: FeedbackItem): {
  id: string;
  name: string;
} {
  if (
    feedback.type.id === "agent_vendor" ||
    feedback.type.id === "price_reduction"
  ) {
    return { id: "agent-" + feedback.agent.id, name: feedback.agent.name };
  }

  if ("group" in feedback && feedback.attendees?.length) {
    return { id: feedback.group.id, name: feedback.group.name };
  }

  return { id: "unknown-" + feedback.id, name: "Unknown" };
}

function groupByName(feedback: FeedbackItem[]) {
  const allGroups = feedback.map(getPartyFromFeedback);
  const uniqueGroups = [
    ...new Map(allGroups.map((group) => [group.id, group])).values(),
  ].sort((a, b) => a.name.localeCompare(b.name));

  return uniqueGroups.map((group) => ({
    label: group.name,
    feedback: sortFeedbackMostRecentFirst(
      feedback.filter((i) => getPartyFromFeedback(i).id === group.id),
    ),
  }));
}

export function groupFeedback(
  feedback: FeedbackItem[],
  groupBy: FeedbackGroupByOption,
) {
  switch (groupBy) {
    case FeedbackGroupByOption.Name:
      return groupByName(feedback);
    case FeedbackGroupByOption.InterestLevel:
      return groupByInterestLevel(feedback);
    case FeedbackGroupByOption.FeedbackType:
      return groupByFeedbackType(feedback);
    case FeedbackGroupByOption.Date:
      return groupByDate(feedback);
  }
}

export function sortFeedbackMostRecentFirst<
  T extends { last_updated_at: string; date_of: string },
>(entries: T[]): T[] {
  return entries
    .sort((a, b) => {
      return (
        new Date(b.last_updated_at).getTime() -
        new Date(a.last_updated_at).getTime()
      );
    })
    .sort((a, b) => {
      return new Date(b.date_of).getTime() - new Date(a.date_of).getTime();
    });
}

type BetweenFilter = {
  between: [number, number];
  inclusive?: "both" | "none" | "lower" | "upper";
};

type GreaterThanFilter = {
  gte: number;
};

type LessThanFilter = {
  lte: number;
};

export type NumberFilter = BetweenFilter | GreaterThanFilter | LessThanFilter;

export type ShowOnlyOptions =
  | {
      key: "interest_level" | "enquiry_source";
      value: EnumValue["id"];
    }
  | {
      key: "repeat_attendee";
      value?: never;
    }
  | {
      key: "price_indication";
      value: NumberFilter;
    };

const feedbackItemIsAgentUpdate = (
  feedback: FeedbackItem,
): feedback is APIListingAgentUpdateEntry => {
  return feedback.type.id === "agent_vendor";
};

const evaluateBetweenFilter = (
  priceIndication: number,
  filter: BetweenFilter,
) => {
  const { between, inclusive = "none" } = filter;
  const [min, max] = between;
  switch (inclusive) {
    case "both":
      return priceIndication >= min && priceIndication <= max;
    case "lower":
      return priceIndication >= min && priceIndication < max;
    case "upper":
      return priceIndication > min && priceIndication <= max;
    case "none":
    default:
      return priceIndication > min && priceIndication < max;
  }
};

export function shouldShowFeedback(
  feedback: FeedbackItem,
  showOnly: ShowOnlyOptions,
) {
  if (feedbackItemIsAgentUpdate(feedback)) {
    // Agent updates do not have interest level, enquiry source, or price indication
    return false;
  }

  const { key, value } = showOnly;

  if (key === "enquiry_source" && "enquiry_source" in feedback) {
    return feedback.enquiry_source?.id === value;
  }

  if (key === "interest_level" && "group" in feedback) {
    return feedback.group?.interest_level?.id === value;
  }

  if (key === "repeat_attendee" && "group" in feedback) {
    return feedback.group?.times_inspected > 1;
  }

  if (key === "price_indication") {
    const priceIndication = feedback.price_indication;

    if (!priceIndication) {
      return false;
    }

    switch (true) {
      case "between" in value:
        return evaluateBetweenFilter(priceIndication, value);
      case "gte" in value:
        return priceIndication >= value.gte;
      case "lte" in value:
        return priceIndication <= value.lte;
      default:
        return false;
    }
  }

  return false;
}
